import { useState, useEffect, useCallback } from "react";
import { Plus, HandCoins, CheckCircle2, Trash2 } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import CertificadoContratoModal from "./CertificadoContratoModal";   // Fix EE (2026-09-24)
import ContratoTerceroModal from "./ContratoTerceroModal";
import { toast } from "sonner";

const opts = { withCredentials: true };

function ContratoCard({ obraId, contrato, tareas = [], canFullAccess, canRegistrarAvance, onChanged, onAdenda }) {
  const [certs, setCerts] = useState([]);
  const [verTodas, setVerTodas] = useState(false);
  const [loading, setLoading] = useState(true);
  const [certModal, setCertModal] = useState(null);   // Fix EE: "nuevo" o el id del certificado
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/obras/${obraId}/contratos-terceros/${contrato.id}/certificaciones`, opts)
      .then(({ data }) => setCerts(data.certificaciones || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [obraId, contrato.id]);
  useEffect(load, [load]);

  // Fix CO (2026-09-13): lo certificado y el saldo del contrato, para cargar el
  // avance sabiendo cuánto queda (como el análisis de compra de materiales).
  const certificado = certs.reduce((s, c) => s + (Number(c.monto) || 0), 0);
  const saldo = (Number(contrato.monto_total_cerrado) || 0) - certificado;
  // Fix DX (2026-09-23): el ítem y el nivel salen de la tarea de la obra.
  const porId = new Map(tareas.map(t => [t.id, t]));
  const lineas = (contrato.tareas || []).map(ct => ({ ...ct, tarea: porId.get(ct.tarea_id) }));


  const validar = async (certId) => {
    setSaving(true);
    try {
      await axios.put(`${API}/obras/${obraId}/contratos-terceros/${contrato.id}/certificaciones/${certId}/validar`, {}, opts);
      toast.success("Certificación validada — OP generada en Tesorería");
      load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al validar"); }
    finally { setSaving(false); }
  };

  // Fix CO (2026-09-13): "Como puedo borrar esto? estaba cargando a modo de prueba"
  // (Edson, 13/09). Se puede borrar el avance (validado o no) y el contrato entero.
  const borrarCert = async (c) => {
    const aviso = c.estado === "borrador"
      ? `¿Borrar el avance #${c.numero}?`
      : `El avance #${c.numero} ya está validado. Al borrarlo se devuelve el consumo a las tareas y la orden de pago ${c.payment_order_number || ""} queda anulada. ¿Continuar?`;
    if (!window.confirm(aviso)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/obras/${obraId}/contratos-terceros/${contrato.id}/certificaciones/${c.id}`, opts);
      toast.success("Avance borrado");
      load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo borrar"); }
    finally { setSaving(false); }
  };

  const borrarContrato = async () => {
    if (!window.confirm(`¿Borrar el contrato de ${contrato.contratista?.nombre || ""}? Se borran también sus avances y se anulan las órdenes de pago que todavía no estén pagadas.`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/obras/${obraId}/contratos-terceros/${contrato.id}`, opts);
      toast.success("Contrato eliminado");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm space-y-3" data-testid={`pagos-contrato-${contrato.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="font-bold text-gray-900">
            {contrato.contratista?.nombre}
            {/* Fix EC (2026-09-24): adendas del contrato. */}
            {contrato.adenda_de && (
              <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700" data-testid={`pagos-adenda-badge-${contrato.id}`}>
                Adenda {contrato.adenda_numero || ""}
              </span>
            )}
          </p>
          {/* Fix DX (2026-09-23): "sería bueno también que a parte de la tarea se detalle
              el ítem y el nivel en el apartado de pagos" (Edson, 22/09). */}
          <p className="text-xs text-gray-400">
            {contrato.contratista?.ruc ? `RUC ${contrato.contratista.ruc} · ` : ""}{lineas.length} tarea(s)
            {canFullAccess && contrato.factor ? ` · factor ${String(contrato.factor).replace(".", ",")}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canFullAccess && (
            <div className="text-right">
              <span className="text-sm font-bold text-gray-700">{formatPYG(contrato.monto_total_cerrado || 0)}</span>
              <span className="block text-[10px] text-gray-400" data-testid={`pagos-saldo-${contrato.id}`}>
                Certificado {formatPYG(certificado)} · Disponible {formatPYG(saldo)}
              </span>
            </div>
          )}
          {canFullAccess && !contrato.adenda_de && onAdenda && (
            <button onClick={() => onAdenda(contrato)} className="flex items-center gap-1 text-xs font-semibold text-[#EA580C] hover:text-[#C2410C]"
              data-testid={`pagos-agregar-adenda-${contrato.id}`}>
              <Plus size={13} /> Adenda
            </button>
          )}
          {canFullAccess && (
            <button onClick={borrarContrato} disabled={saving} title="Borrar contrato"
              className="text-gray-300 hover:text-red-600 disabled:opacity-40" data-testid={`pagos-borrar-contrato-${contrato.id}`}>
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </div>

      <div className="text-[11px] text-gray-500 space-y-0.5" data-testid={`pagos-tareas-${contrato.id}`}>
        {(verTodas ? lineas : lineas.slice(0, 5)).map(l => (
          <p key={l.tarea_id} className="truncate">
            <span className="font-semibold text-gray-700">{l.tarea?.item_numero || "—"}</span>
            {l.tarea?.nivel_label ? ` · ${l.tarea.nivel_label}` : ""} — {l.tarea_nombre}
            {!l.tarea && <span className="text-red-600 font-semibold"> (la tarea ya no está en la obra)</span>}
            {canFullAccess && <span className="text-gray-400"> · {formatPYG(l.monto_cerrado || 0)}</span>}
          </p>
        ))}
        {lineas.length > 5 && (
          <button type="button" onClick={() => setVerTodas(v => !v)} className="text-[#2E6DA4] font-semibold hover:underline" data-testid={`pagos-ver-tareas-${contrato.id}`}>
            {verTodas ? "Ver menos" : `Ver las ${lineas.length} tareas`}
          </button>
        )}
      </div>

      {loading ? <Skeleton className="h-8 w-full" /> : (
        <div className="space-y-1.5">
          {certs.map(c => (
            <div key={c.id} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2" data-testid={`pagos-cert-${c.id}`}>
              <span>
                {/* Fix EE (2026-09-24): se ve (y se descarga) por tarea. */}
                <button onClick={() => setCertModal(c.id)} className="font-semibold text-[#2E6DA4] hover:underline" data-testid={`pagos-ver-cert-${c.id}`}>
                  Certificado #{c.numero}
                </button>
                {" "}— {c.pct_avance}%{c.fecha ? ` · ${String(c.fecha).slice(0, 10).split("-").reverse().join("/")}` : ""}
                {c.excede_fondo && <span className="text-red-600 font-bold ml-1">excede el fondo</span>}
              </span>
              <div className="flex items-center gap-2">
                {canFullAccess && <span className="font-semibold">{formatPYG(c.monto || 0)}</span>}
                {c.estado === "borrador" ? (
                  canFullAccess && (
                    <button onClick={() => validar(c.id)} disabled={saving} className="flex items-center gap-1 text-emerald-600 hover:text-emerald-800 font-bold" data-testid={`pagos-validar-btn-${c.id}`}>
                      <CheckCircle2 size={13} /> Validar y pagar
                    </button>
                  )
                ) : c.estado === "validando" ? (
                  <span className="text-amber-600 font-bold">Validando…</span>
                ) : (
                  <span className="text-emerald-700 font-bold">Validado — {c.payment_order_number}</span>
                )}
                {(canFullAccess || c.estado === "borrador") && (
                  <button onClick={() => borrarCert(c)} disabled={saving} title="Borrar avance"
                    className="text-gray-300 hover:text-red-600 disabled:opacity-40" data-testid={`pagos-borrar-cert-${c.id}`}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {canRegistrarAvance && (
        <div className="flex items-center gap-2 pt-1 flex-wrap">
          {/* Fix EE (2026-09-24): el certificado se carga por tarea (% actual). */}
          <button onClick={() => setCertModal("nuevo")} disabled={saving} className="text-xs font-semibold text-[#EA580C] hover:text-[#C2410C] flex items-center gap-1" data-testid={`pagos-nuevo-cert-btn-${contrato.id}`}>
            <Plus size={13} /> Nuevo certificado
          </button>
          {canFullAccess && (
            <span className="text-[10px] text-gray-400" data-testid={`pagos-disponible-${contrato.id}`}>
              Disponible del contrato: {formatPYG(saldo)}
            </span>
          )}
        </div>
      )}
      {certModal && (
        <CertificadoContratoModal obraId={obraId} contrato={contrato} certId={certModal === "nuevo" ? null : certModal}
          canMoney={canFullAccess} onClose={() => setCertModal(null)} onSaved={() => { load(); onChanged(); }} />
      )}
    </div>
  );
}

export default function PagosTab({ obraId, canFullAccess, isResidente }) {
  const [contratos, setContratos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [adendaDe, setAdendaDe] = useState(null);   // Fix EC (2026-09-24)
  // Fix EF (2026-09-24): deja el consumo de terceros de cada tarea igual a lo validado.
  const [recalculando, setRecalculando] = useState(false);
  const recalcular = async () => {
    if (!window.confirm("Recalcula el consumo de terceros de todas las tareas de esta obra con lo validado en sus contratos. ¿Continuar?")) return;
    setRecalculando(true);
    try {
      const { data } = await axios.post(`${API}/obras/${obraId}/contratos-terceros/recalcular-consumo`, {}, opts);
      toast.success(`Consumo recalculado: ${data.tareas_corregidas} tarea(s) corregida(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo recalcular"); }
    finally { setRecalculando(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/obras/${obraId}/contratos-terceros`, opts),
      axios.get(`${API}/obras/${obraId}/tareas`, opts),
    ]).then(([c, t]) => { setContratos(c.data.contratos || []); setTareas(t.data.tareas || []); })
      .catch(() => toast.error("Error cargando pagos"))
      .finally(() => setLoading(false));
  }, [obraId]);
  useEffect(load, [load]);

  if (loading) return <div className="space-y-2">{[1, 2].map(i => <Skeleton key={i} className="h-24 w-full rounded-2xl" />)}</div>;

  return (
    <div className="space-y-4" data-testid="obra-pagos-tab">
      {canFullAccess && (
        <div className="flex justify-end gap-2">
          <button onClick={recalcular} disabled={recalculando} className="flex items-center gap-1.5 border border-gray-300 text-gray-600 hover:border-[#EA580C] rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50" data-testid="pagos-recalcular-btn">
            {recalculando ? "Recalculando…" : "Recalcular consumo de terceros"}
          </button>
          <button onClick={() => setShowModal(true)} className="flex items-center gap-1.5 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid="pagos-nuevo-contrato-btn">
            <Plus size={13} /> Nuevo contrato con tercero
          </button>
        </div>
      )}
      {contratos.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-8 text-center shadow-sm">
          <HandCoins size={36} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-600 font-medium">Sin contratos con terceros todavía</p>
        </div>
      ) : [
        // Fix EC (2026-09-24): cada contrato seguido de sus adendas, en orden.
        ...contratos.filter(c => !c.adenda_de).flatMap(c => [c, ...contratos
          .filter(a => a.adenda_de === c.id).sort((a, b) => (a.adenda_numero || 0) - (b.adenda_numero || 0))]),
        ...contratos.filter(a => a.adenda_de && !contratos.some(c => c.id === a.adenda_de)),
      ].map(c => (
        <ContratoCard key={c.id} obraId={obraId} contrato={c} tareas={tareas} canFullAccess={canFullAccess} canRegistrarAvance={canFullAccess || isResidente} onChanged={load} onAdenda={setAdendaDe} />
      ))}
      {showModal && (
        <ContratoTerceroModal obraId={obraId} tareas={tareas} onClose={() => setShowModal(false)} onSaved={load} />
      )}
      {adendaDe && (
        <ContratoTerceroModal obraId={obraId} tareas={tareas} adendaDe={adendaDe} onClose={() => setAdendaDe(null)} onSaved={load} />
      )}
    </div>
  );
}
